import { useState } from "react";
import { Copy, Check, ExternalLink, Link2, Share2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

const WaIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}><path d="M17.5 14.4c-.3-.1-1.7-.8-2-.9-.3-.1-.5-.1-.7.1-.2.3-.8.9-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.2-.5-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6l.4-.5c.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5 2.5 1 3 .8 3.6.7.6-.1 1.7-.7 2-1.4.2-.7.2-1.2.2-1.4-.1-.1-.3-.2-.6-.3zM12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2z" /></svg>
);

export const usePublicLink = (org) => {
  const url = `${window.location.origin}/book/${org?.slug}`;
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const t = document.createElement("textarea");
      t.value = url; document.body.appendChild(t); t.select(); document.execCommand("copy"); t.remove();
    }
    setCopied(true);
    toast.success("Link copiado");
    setTimeout(() => setCopied(false), 2000);
  };
  const waHref = `https://wa.me/?text=${encodeURIComponent(`¡Reservá tu turno en ${org?.name} online! ${url}`)}`;
  return { url, copied, copy, waHref };
};

const ActionBtn = ({ children, className = "", ...p }) => (
  <a className={`inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md text-xs uppercase tracking-widest font-bold transition-colors ${className}`} {...p}>{children}</a>
);

export const PublicLinkBanner = ({ org }) => {
  const { url, copied, copy, waHref } = usePublicLink(org);
  return (
    <div className="relative overflow-hidden rounded-xl border-2 border-emerald-500 bg-emerald-500/10 p-6 md:p-8 mb-10" data-testid="public-link-banner">
      <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-emerald-500/20 blur-3xl" />
      <div className="relative">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-emerald-400 font-bold mb-2">
          <Link2 className="h-4 w-4" /> Link para tus clientes
        </div>
        <p className="text-zinc-300 text-sm mb-4">Compartí este link para que tus clientes reserven turnos online 24/7.</p>
        <div className="font-mono text-base md:text-xl text-white bg-black/40 border border-emerald-500/40 rounded-md px-4 py-3 break-all mb-5" data-testid="public-link-url">{url}</div>
        <div className="flex flex-wrap gap-3">
          <button onClick={copy} className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md text-xs uppercase tracking-widest font-bold transition-colors bg-emerald-500 hover:bg-emerald-600 text-black" data-testid="public-link-copy-btn">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copiado" : "Copiar link"}
          </button>
          <ActionBtn href={waHref} target="_blank" rel="noreferrer" className="bg-[#25D366] hover:bg-[#1ebe5a] text-black" data-testid="public-link-whatsapp-btn">
            <WaIcon className="h-4 w-4" /> Compartir por WhatsApp
          </ActionBtn>
          <ActionBtn href={url} target="_blank" rel="noreferrer" className="border border-zinc-600 text-white hover:bg-white/10" data-testid="public-link-open-btn">
            <ExternalLink className="h-4 w-4" /> Abrir
          </ActionBtn>
        </div>
      </div>
    </div>
  );
};

export const PublicLinkButton = ({ org, compact }) => {
  const { url, copied, copy, waHref } = usePublicLink(org);
  const item = "flex items-center gap-3 w-full px-3 py-2.5 rounded text-sm hover:bg-white/10 transition-colors";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="inline-flex items-center gap-2 h-9 px-3 rounded-md bg-emerald-500 hover:bg-emerald-600 text-black text-xs uppercase tracking-widest font-bold transition-colors" data-testid={compact ? "header-public-link-btn-mobile" : "header-public-link-btn"}>
          <Share2 className="h-4 w-4" /> {compact ? "Link" : "Link para clientes"}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 bg-[#141414] border-[#262626] text-white p-3" data-testid="header-public-link-popover">
        <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400 font-bold mb-2">Tu link público</div>
        <div className="font-mono text-xs bg-black/40 border border-[#262626] rounded px-3 py-2 break-all mb-2">{url}</div>
        <button onClick={copy} className={item} data-testid="header-link-copy">
          {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />} {copied ? "Copiado" : "Copiar link"}
        </button>
        <a href={waHref} target="_blank" rel="noreferrer" className={item} data-testid="header-link-whatsapp">
          <WaIcon className="h-4 w-4 text-[#25D366]" /> Compartir por WhatsApp
        </a>
        <a href={url} target="_blank" rel="noreferrer" className={item} data-testid="header-link-open">
          <ExternalLink className="h-4 w-4" /> Abrir página
        </a>
      </PopoverContent>
    </Popover>
  );
};
